import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Package,
  MessageSquare,
  Mail,
  TrendingUp,
  Search,
  Trash2,
  Phone,
  ExternalLink,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const STATUS_COLORS = {
  new: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  contacted: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  quoted: "bg-purple-500/10 text-purple-700 dark:text-purple-300",
  won: "bg-green-500/10 text-green-700 dark:text-green-300",
  lost: "bg-red-500/10 text-red-700 dark:text-red-300",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [chats, setChats] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [openChat, setOpenChat] = useState(null);

  const email = localStorage.getItem("el_email");

  const logout = () => {
    localStorage.removeItem("el_token");
    localStorage.removeItem("el_email");
    nav("/admin/login");
  };

  const load = async () => {
    try {
      const [s, l, c, ch] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/admin/contacts"),
        api.get("/admin/chats"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setContacts(c.data);
      setChats(ch.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Failed to load dashboard.");
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("el_token")) {
      nav("/admin/login");
      return;
    }
    load();
     
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Lead updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const filteredLeads = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter !== "all" && l.status !== filter) return false;
      if (!q) return true;
      return [l.name, l.phone, l.email, l.from_city, l.to_city, l.service_type]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [leads, query, filter]);

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Topbar */}
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-bold text-sm">
              E
            </div>
            <div className="font-display text-base">Express Logistics · Admin</div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-xs text-muted-foreground" data-testid="admin-email-label">
              {email}
            </div>
            <Button
              data-testid="admin-logout-btn"
              size="sm"
              variant="outline"
              onClick={logout}
              className="rounded-full h-8"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <h1 className="font-display text-3xl mb-6">Dashboard</h1>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <StatCard icon={Package} label="Total leads" value={stats?.total_leads ?? "—"} />
          <StatCard icon={TrendingUp} label="New leads" value={stats?.new_leads ?? "—"} accent />
          <StatCard icon={Mail} label="Contacts" value={stats?.total_contacts ?? "—"} />
          <StatCard icon={MessageSquare} label="Chat sessions" value={stats?.total_chats ?? "—"} />
        </div>

        {stats?.by_service?.length > 0 && (
          <div className="rounded-2xl bg-background border border-border p-6 mb-8">
            <div className="font-display text-lg mb-4">Leads by service</div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={stats.by_service}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="service" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--background))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                    }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <Tabs defaultValue="leads" className="space-y-4">
          <TabsList className="bg-background border border-border rounded-full p-1 h-11">
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Leads
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Contacts
            </TabsTrigger>
            <TabsTrigger value="chats" data-testid="tab-chats" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              AI Chats
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <div className="rounded-2xl bg-background border border-border overflow-hidden">
              <div className="p-4 flex flex-wrap gap-3 items-center border-b border-border">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid="lead-search"
                    placeholder="Search by name, phone, city…"
                    className="pl-9"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger data-testid="lead-filter" className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="quoted">Quoted</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                          No leads yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredLeads.map((l) => (
                      <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                        <TableCell>
                          <div className="font-medium">{l.name}</div>
                          <div className="text-xs text-muted-foreground">{l.email || "—"}</div>
                        </TableCell>
                        <TableCell>
                          <a href={`tel:${l.phone}`} className="hover:text-primary inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {l.phone}
                          </a>
                        </TableCell>
                        <TableCell>{l.from_city} → {l.to_city}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{l.service_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                            <SelectTrigger className={`w-32 h-8 text-xs ${STATUS_COLORS[l.status] || ""}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="quoted">Quoted</SelectItem>
                              <SelectItem value="won">Won</SelectItem>
                              <SelectItem value="lost">Lost</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(l.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <button
                            data-testid={`lead-delete-${l.id}`}
                            onClick={() => deleteLead(l.id)}
                            className="p-2 rounded hover:bg-muted"
                          >
                            <Trash2 className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="rounded-2xl bg-background border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-10">
                        No contact submissions yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {contacts.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.subject || "—"}</TableCell>
                      <TableCell className="max-w-md truncate">{c.message}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(c.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="chats">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {chats.length === 0 && (
                <div className="col-span-full text-center text-muted-foreground py-16 rounded-2xl bg-background border border-border">
                  No chat sessions yet.
                </div>
              )}
              {chats.map((c) => (
                <button
                  key={c.session_id}
                  onClick={() => setOpenChat(c)}
                  data-testid={`chat-card-${c.session_id}`}
                  className="text-left rounded-2xl bg-background border border-border p-5 hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow]"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs text-muted-foreground">
                      {new Date(c.created_at).toLocaleString()}
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <div className="font-mono text-xs text-muted-foreground truncate">{c.session_id}</div>
                  <div className="mt-3 text-sm line-clamp-3">
                    {(c.messages || []).slice(-1)[0]?.text || "—"}
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">
                    {(c.messages || []).length} messages
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!openChat} onOpenChange={(o) => !o && setOpenChat(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Conversation</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-3 p-1">
            {(openChat?.messages || []).map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-muted rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl p-6 border border-border ${accent ? "bg-primary text-primary-foreground" : "bg-background"}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest opacity-70">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="mt-3 font-display text-3xl">{value}</div>
    </div>
  );
}
